import React, { FC, useRef, useState, useCallback } from 'react'
import {
  InputGroup,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  useDisclosure,
  ResponsiveValue,
} from '@chakra-ui/react'
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete'
import { SearchIcon } from '@chakra-ui/icons'
import { theme } from 'src/themes'
import { searchModelsApi } from 'server/api/common'

interface Props {
  items?: Array<{ label: string | number, value: any }>
  apiPath?: string | null
  onSelect: any
  placeholder?: string
  notFoundText?: string
  withOverlay?: boolean
  m?: ResponsiveValue<any>
  mt?: ResponsiveValue<any>
  mb?: ResponsiveValue<any>
  mx?: ResponsiveValue<any>
  my?: ResponsiveValue<any>
  w?: ResponsiveValue<any>
  width?: ResponsiveValue<any>
  minW?: ResponsiveValue<any>
  minWidth?: ResponsiveValue<any>
  disableFilter?: true
  resultFormatter?: () => { label: any; value: any };
}

const Autocomplete: FC<Props> = ({ items, apiPath, onSelect, placeholder, notFoundText, withOverlay, disableFilter, resultFormatter, ...styleProps }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const searchInputElement = useRef<HTMLInputElement>(null)
  const modalClosedFocusRef = useRef<HTMLDivElement>(null)

  const [searchTerm, setSearchTerm] = useState<any>([])

  let itemsRepo = apiPath ? searchTerm : items;

  const searchInputChangeHandler = useCallback(async (target) => {
    const { value } = target;

    if (apiPath && value.length >= MIN_STRING_LENGTH) {
      let found = await searchModelsApi(apiPath, value)

      if (found && found.data) {
        itemsRepo = found.data.map(resultFormatter || (({ name, code }) => ({ label: name, value: code })));
      }

      setSearchTerm(itemsRepo)
    }
  }, [])

  const MIN_STRING_LENGTH = 2

  const searchInputFocusHandler = () => {
    if (withOverlay) {
      if (searchInputElement && searchInputElement.current) {
        searchInputElement.current.style.zIndex = '1401'; // modal z-index + 1
      }
      onOpen();
    }
  }

  const onModalClose = () => {
    if (withOverlay) {
      if (searchInputElement && searchInputElement.current) {
        searchInputElement.current.style.zIndex = '1';
      }
      onClose();
    }
  }

  return <>
    <InputGroup
      size="lg"
      display="block"
      ref={searchInputElement}
      onFocus={searchInputFocusHandler}
      borderColor={theme.colors.borders.grey}
      className="searchAutoComplete"
      {...styleProps}
    >
      <AutoComplete
        onChange={onSelect}
        emptyState={<em>{notFoundText || ''}</em>}
        filter={disableFilter ? () => true : null}
        shouldRenderSuggestions={value => value.length >= MIN_STRING_LENGTH}
      >
        <AutoCompleteInput
          size="lg"
          placeholder={placeholder}
          borderRadius="4px"
          className="auto-complete-input"
          onChange={({ target }) => searchInputChangeHandler(target)}
        />
        <AutoCompleteList borderRadius="4px" mt="0" maxWidth="100%">
          {itemsRepo.map(item => (
            <AutoCompleteItem
              key={`option-${item.value}`}
              value={item.value}
              label={`${item.label}`}
              textAlign="left"
            >
              {`${item.label.substring(0, 70)}…`}
            </AutoCompleteItem>
          ))}
        </AutoCompleteList>
      </AutoComplete>
      <InputRightElement><SearchIcon color="baseNeutral.400" /></InputRightElement>
    </InputGroup>

    {withOverlay &&
      <Modal isOpen={isOpen} onClose={onModalClose} trapFocus={false} finalFocusRef={modalClosedFocusRef}>
        <ModalOverlay />
        <ModalContent display="none" top="-1000px" left="-1000px" />
      </Modal>
    }
  </>
}

export default Autocomplete
